<template>
    <div>
        <ts-page-title
            title="Daily Collection Schedule"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                { text: 'Daily Collection Schedule', active: true }
            ]"
        />

        <!-- ────────────────── FILTER PANEL ────────────────── -->
        <ts-panel>
            <ts-panel-wrapper class="tw-py-3">
                <div
                    class="tw-flex tw-justify-between tw-items-center tw-cursor-pointer tw-select-none"
                    @click="filtersOpen = !filtersOpen"
                >
                    <div class="tw-flex tw-items-center tw-space-x-3 tw-flex-1 tw-min-w-0">
                        <i
                            class="fas tw-text-gray-400"
                            :class="filtersOpen ? 'fa-chevron-down' : 'fa-chevron-right'"
                            style="width: 12px"
                        ></i>
                        <b class="tw-text-gray-700 tw-whitespace-nowrap">Filter Criteria</b>
                        <div v-if="activeChips.length" class="tw-flex tw-flex-wrap tw-gap-1 tw-flex-1 tw-min-w-0">
                            <span
                                v-for="chip in activeChips"
                                :key="chip.key"
                                class="chip-pill"
                                :class="chip.tone || 'chip-default'"
                            >
                                <i v-if="chip.icon" :class="'fas ' + chip.icon + ' tw-mr-1'"></i>
                                {{ chip.label }}
                            </span>
                        </div>
                    </div>
                    <span class="tw-text-sm tw-text-blue-600 tw-whitespace-nowrap tw-ml-3">
                        {{ filtersOpen ? 'Collapse' : 'Edit filters' }}
                    </span>
                </div>

                <div v-show="filtersOpen" class="tw-mt-3">
                    <!-- Row 1: Classified / Branch / CO / Currency -->
                    <div class="filter-grid">
                        <div class="filter-cell" style="grid-column: span 3">
                            <label class="filter-lbl">Classified By</label>
                            <div class="pill-group">
                                <button
                                    v-for="m in classifiedByOptions"
                                    :key="m.value"
                                    type="button"
                                    class="pill"
                                    :class="{ 'pill-active': model.classified_by === m.value }"
                                    @click="model.classified_by = m.value"
                                >
                                    <i :class="'fas ' + m.icon + ' tw-mr-1'"></i>
                                    {{ m.label }}
                                </button>
                            </div>
                        </div>

                        <div class="filter-cell" style="grid-column: span 3">
                            <label class="filter-lbl">Branch <span class="tw-text-gray-400 tw-font-normal tw-normal-case">(all if empty)</span></label>
                            <ts-branch-filter
                                @filter="value => (model.branch_id = value)"
                                :isPlaceholder="true"
                            />
                        </div>

                        <div class="filter-cell" style="grid-column: span 4">
                            <label class="filter-lbl">Credit Officer</label>
                            <Select
                                v-model="model.co_id"
                                multiple
                                :max-tag-count="1"
                                :filterable="true"
                                :filter-by-label="true"
                                :disabled="isBranchMode"
                                placeholder="All COs"
                            >
                                <Option
                                    v-for="item in employees"
                                    :value="item.employee_id"
                                    :key="item.employee_id"
                                    :label="item.employee_name_en"
                                >
                                    {{ item.employee_name_en }}
                                </Option>
                            </Select>
                        </div>

                        <div class="filter-cell" style="grid-column: span 2">
                            <label class="filter-lbl">Currency <span class="text-danger">*</span></label>
                            <div class="pill-group">
                                <button
                                    v-for="c in currencies"
                                    :key="c.currency_id"
                                    type="button"
                                    class="pill"
                                    :class="{ 'pill-active': model.currency_id === c.currency_id }"
                                    @click="model.currency_id = c.currency_id"
                                >{{ c.currency_code }}</button>
                            </div>
                        </div>
                    </div>

                    <!-- Row 2: Paid filter / Search -->
                    <div class="filter-grid tw-mt-3 tw-items-end">
                        <div class="filter-cell" style="grid-column: span 4">
                            <label class="filter-lbl">Paid Status</label>
                            <div class="pill-group">
                                <button type="button" class="pill"
                                    :class="{ 'pill-active': model.paid_filter === 'all' }"
                                    @click="model.paid_filter = 'all'">All</button>
                                <button type="button" class="pill"
                                    :class="{ 'pill-active': model.paid_filter === 'paid' }"
                                    @click="model.paid_filter = 'paid'">Collected</button>
                                <button type="button" class="pill"
                                    :class="{ 'pill-active': model.paid_filter === 'pending' }"
                                    @click="model.paid_filter = 'pending'">Pending</button>
                            </div>
                        </div>

                        <div class="filter-cell" style="grid-column: span 8">
                            <label class="filter-lbl">Search</label>
                            <div class="search-group">
                                <div class="search-input-wrap">
                                    <input type="text" class="form-control search-input"
                                        v-model="searchQuery"
                                        :placeholder="isBranchMode ? 'Branch code or name' : 'Loan #, customer code / name, CO name'"
                                        @keyup.enter="onSearch"/>
                                    <button v-if="searchQuery" type="button" class="search-clear"
                                        title="Clear search" @click.prevent="onClearSearch">
                                        <i class="fas fa-times-circle"></i>
                                    </button>
                                </div>
                                <button type="button" class="search-btn" :disabled="loading" @click.prevent="onSearch">
                                    <i class="fas fa-circle-notch fa-spin" v-if="loading"></i>
                                    <i class="fas fa-search" v-else></i>
                                    <span class="tw-ml-1">Search</span>
                                </button>
                                <button type="button" class="reset-btn" @click.prevent="onReset" title="Reset all filters">
                                    <i class="fas fa-undo"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </ts-panel-wrapper>
        </ts-panel>

        <!-- ────────────────── SUMMARY CARDS ────────────────── -->
        <div class="summary-grid tw-mt-3">
            <div
                v-for="card in summaryCards"
                :key="card.key"
                class="summary-card"
                :class="'summary-card--' + card.tone"
            >
                <div class="summary-card__header">
                    <div class="summary-card__icon"><i :class="'fas ' + card.icon"></i></div>
                    <div class="summary-card__label">{{ card.label }}</div>
                    <div v-if="card.badge !== undefined" class="summary-card__count">
                        <template v-if="summaryLoading">…</template>
                        <template v-else>{{ card.badge }}</template>
                    </div>
                </div>
                <div class="summary-card__value">
                    <template v-if="summaryLoading">
                        <span class="tw-text-gray-300">—</span>
                    </template>
                    <template v-else>
                        <span class="summary-card__amount">{{ card.formatted }}</span>
                        <span class="summary-card__currency">{{ card.unit }}</span>
                    </template>
                </div>
            </div>
        </div>

        <!-- ────────────────── RESULTS CARD ────────────────── -->
        <div class="results-card tw-mt-3">
            <div class="results-toolbar">
                <div class="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-flex-wrap">
                    <template v-if="loading">
                        <span class="tw-text-gray-500">
                            <i class="fas fa-circle-notch fa-spin tw-mr-1"></i> Loading…
                        </span>
                    </template>
                    <template v-else-if="meta.total === null">
                        <span class="tw-text-gray-400">
                            <i class="fas fa-info-circle tw-mr-1"></i> No search yet
                        </span>
                    </template>
                    <template v-else-if="meta.total === 0">
                        <span class="tw-text-gray-500">
                            <i class="far fa-folder-open tw-mr-1"></i> No results
                        </span>
                    </template>
                    <template v-else>
                        <span class="tw-font-bold tw-text-gray-800 tw-text-base">
                            {{ meta.total.toLocaleString() }} rows
                        </span>
                        <span class="tw-text-gray-500">
                            · Page {{ meta.current_page }} of {{ meta.last_page }}
                        </span>
                    </template>
                </div>

                <div class="export-group" title="Export the current filters to xlsx">
                    <select v-model="model.excel_template" class="export-select">
                        <option value="standard">Standard xlsx</option>
                        <option value="raw-data">Raw Data xlsx</option>
                    </select>
                    <button
                        type="button"
                        class="export-btn"
                        :disabled="exporting"
                        @click.prevent="exportExcel"
                    >
                        <i class="fas fa-circle-notch fa-spin" v-if="exporting"></i>
                        <i class="far fa-file-excel" v-else></i>
                        <span class="tw-ml-1">Export</span>
                    </button>
                </div>
            </div>

            <div
                class="results-body tw-relative"
                :style="{ minHeight: hasRows ? '0px' : emptyBodyHeight + 'px' }"
            >
                <Spin size="large" fix v-if="loading"></Spin>

                <div v-if="!loading && meta.total === null" class="empty-state">
                    <i class="fas fa-search tw-text-2xl tw-mb-2 tw-text-gray-300"></i>
                    <div class="tw-text-sm tw-text-gray-500">Ready when you are — click <b>Search</b> above.</div>
                </div>

                <div v-else-if="!loading && meta.total === 0" class="empty-state">
                    <i class="far fa-folder-open tw-text-2xl tw-mb-2 tw-text-gray-300"></i>
                    <div class="tw-text-sm tw-text-gray-500">No collection schedule entries for the selected filters.</div>
                </div>

                <Table
                    v-else
                    :columns="columns"
                    :data="rows"
                    border
                    stripe
                    size="small"
                    :height="tableHeight"
                    show-summary
                    :summary-method="summaryMethod"
                    @on-sort-change="onSortChange"
                ></Table>
            </div>

            <div v-if="meta.total > 0" class="results-pagination">
                <div class="pagination-left">
                    <span class="pagination-lbl">Per page:</span>
                    <Select
                        v-model="perPage"
                        @on-change="onPerPageChange"
                        style="width: 80px"
                        size="small"
                    >
                        <Option :value="10">10</Option>
                        <Option :value="30">30</Option>
                        <Option :value="50">50</Option>
                        <Option :value="100">100</Option>
                    </Select>
                    <span class="pagination-divider">|</span>
                    <span class="pagination-lbl">
                        Showing <b>{{ rangeStart.toLocaleString() }}</b>–<b>{{ rangeEnd.toLocaleString() }}</b>
                        of <b>{{ meta.total.toLocaleString() }}</b>
                    </span>
                </div>
                <Page
                    :current="meta.current_page"
                    :total="meta.total || 0"
                    :page-size="perPage"
                    @on-change="onPageChange"
                    size="small"
                    show-elevator
                ></Page>
            </div>
        </div>
    </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import { debounce, trim } from 'lodash'
import moment from 'moment'
import * as FileDownload from 'downloadjs'

export default {
    name: 'dailyCollectionScheduleV3',
    data () {
        return {
            filtersOpen: true,
            loading: false,
            exporting: false,
            errors: new Errors(),
            searchQuery: '',
            perPage: 30,
            viewportHeight: window.innerHeight,
            summaryLoading: false,
            summary: {
                all:       { count: 0, total: '0' },
                collected: { count: 0, total: '0' },
                pending:   { count: 0, total: '0' },
                customers: { count: 0 }
            },
            totals: {
                repayment_amount:      '0',
                actual_payment_amount: '0',
                pay_off_amount:        '0',
                outstanding_amount:    '0',
                loan_count:            null
            },
            mode: 'co',
            rows: [],
            meta: { current_page: 1, last_page: 1, total: null },
            classifiedByOptions: [
                { value: 'co',     label: 'By CO',     icon: 'fa-user' },
                { value: 'branch', label: 'By Branch', icon: 'fa-building' }
            ],
            model: {
                branch_id: [],
                co_id: [],
                currency_id: null,
                classified_by: 'co',
                paid_filter: 'all',
                search: null,
                sort: 'customer_code',
                sort_dir: 'asc',
                page: 1,
                perPage: 30,
                excel_template: 'standard'
            }
        }
    },
    created () {
        this.model.currency_id = this.$store.state.authUser.sys_setting[0].default_currency_id
    },
    computed: {
        ...mapState('report/resource', ['employees', 'currencies']),

        currencyCode () {
            if (!this.model.currency_id) return ''
            const c = (this.currencies || []).find(c => c.currency_id === this.model.currency_id)
            return c ? c.currency_code : ''
        },
        isKh () {
            const locale = (this.$i18n && this.$i18n.locale) || localStorage.getItem('langCode')
            return locale === 'kh'
        },
        isBranchMode () {
            return this.model.classified_by === 'branch'
        },

        activeChips () {
            const chips = []
            if (this.model.branch_id && this.model.branch_id.length) {
                chips.push({ key: 'branch', icon: 'fa-building',
                    label: this.model.branch_id.length + ' branch' + (this.model.branch_id.length > 1 ? 'es' : ''),
                    tone: 'chip-branch' })
            } else {
                chips.push({ key: 'branch', icon: 'fa-building', label: 'All branches', tone: 'chip-branch' })
            }
            if (this.model.currency_id) {
                const cur = this.currencies.find(c => c.currency_id === this.model.currency_id)
                if (cur) chips.push({ key: 'currency', label: cur.currency_code, tone: 'chip-currency' })
            }
            chips.push({
                key: 'mode',
                icon: this.isBranchMode ? 'fa-building' : 'fa-user',
                label: 'By ' + (this.isBranchMode ? 'Branch' : 'CO'),
                tone: 'chip-type'
            })
            if (!this.isBranchMode && this.model.co_id && this.model.co_id.length) {
                chips.push({ key: 'co', icon: 'fa-user',
                    label: this.model.co_id.length + ' CO' + (this.model.co_id.length > 1 ? 's' : ''),
                    tone: 'chip-co' })
            }
            if (this.model.paid_filter !== 'all') {
                chips.push({ key: 'paid', icon: 'fa-filter',
                    label: this.model.paid_filter === 'paid' ? 'Collected' : 'Pending',
                    tone: 'chip-search' })
            }
            if (this.searchQuery && this.searchQuery.trim().length >= 2) {
                chips.push({ key: 'search', icon: 'fa-search', label: '"' + this.searchQuery.trim() + '"', tone: 'chip-search' })
            }
            return chips.filter(c => c.label)
        },

        tableHeight () {
            const chrome = this.filtersOpen ? 601 : 471
            const h = this.viewportHeight - chrome
            return Math.max(280, h)
        },
        emptyBodyHeight () {
            const PAGINATION_ROW = 52
            return this.tableHeight + PAGINATION_ROW
        },
        hasRows () {
            return !this.loading && this.meta.total > 0
        },
        rangeStart () {
            if (!this.meta.total) return 0
            return (this.meta.current_page - 1) * this.perPage + 1
        },
        rangeEnd () {
            if (!this.meta.total) return 0
            return Math.min(this.meta.current_page * this.perPage, this.meta.total)
        },

        summaryCards () {
            const s = this.summary
            return [
                {
                    key: 'all', tone: 'cur_due', icon: 'fa-list-check',
                    label: "Today's Schedule",
                    badge: (s.all.count || 0).toLocaleString(),
                    formatted: this.fmt(s.all.total),
                    unit: this.currencyCode
                },
                {
                    key: 'collected', tone: 'cur_paid', icon: 'fa-circle-check',
                    label: 'Collected Today',
                    badge: (s.collected.count || 0).toLocaleString(),
                    formatted: this.fmt(s.collected.total),
                    unit: this.currencyCode
                },
                {
                    key: 'pending', tone: 'past_due', icon: 'fa-clock',
                    label: 'Pending',
                    badge: (s.pending.count || 0).toLocaleString(),
                    formatted: this.fmt(s.pending.total),
                    unit: this.currencyCode
                },
                {
                    key: 'cust', tone: 'early', icon: 'fa-users',
                    label: 'Customers',
                    formatted: (s.customers.count || 0).toLocaleString(),
                    unit: 'unique'
                }
            ]
        },

        columns () {
            if (this.isBranchMode) {
                return [
                    {
                        title: 'Branch', key: 'branch_code', width: 100, fixed: 'left', align: 'center', sortable: 'custom',
                        render: (h, params) => h('span', { class: 'branch-code' }, params.row.branch_code || '—')
                    },
                    {
                        title: 'Branch Name', key: 'branch_name_en', minWidth: 200, fixed: 'left',
                        render: (h, params) => h('span', { class: 'name-text' }, this.isKh
                            ? (params.row.branch_name_kh || params.row.branch_name_en || '—')
                            : (params.row.branch_name_en || params.row.branch_name_kh || '—'))
                    },
                    { title: '# Loans', key: 'loan_count', width: 100, align: 'right', sortable: 'custom',
                      render: (h, params) => h('span', (parseInt(params.row.loan_count) || 0).toLocaleString()) },
                    { title: '# Customers', key: 'customer_count', width: 110, align: 'right',
                      render: (h, params) => h('span', (parseInt(params.row.customer_count) || 0).toLocaleString()) },
                    { title: 'Repayment Amt', key: 'repayment_amount', width: 150, align: 'right', sortable: 'custom',
                      render: (h, params) => this.moneyCell(params.row.repayment_amount) },
                    { title: 'Collected Today', key: 'actual_payment_amount', width: 160, align: 'right', sortable: 'custom',
                      render: (h, params) => this.moneyCell(params.row.actual_payment_amount) },
                    { title: 'Pay Off Amt', key: 'pay_off_amount', width: 150, align: 'right', sortable: 'custom',
                      render: (h, params) => this.moneyCell(params.row.pay_off_amount) },
                    { title: 'Outstanding', key: 'outstanding_amount', minWidth: 160, align: 'right', sortable: 'custom',
                      render: (h, params) => this.moneyCell(params.row.outstanding_amount) }
                ]
            }
            return [
                {
                    title: 'Branch', key: 'branch_code', width: 90, fixed: 'left', align: 'center', sortable: 'custom',
                    render: (h, params) => h('span', { class: 'branch-code' }, params.row.branch_code || '—')
                },
                {
                    title: 'Loan Account #', key: 'loan_account_no', width: 150, fixed: 'left', sortable: 'custom',
                    render: (h, params) => h('code', { class: 'tw-text-xs tw-text-gray-700' }, params.row.loan_account_no || '—')
                },
                { title: 'Customer Code', key: 'customer_code', width: 130, sortable: 'custom',
                  render: (h, params) => h('code', { class: 'tw-text-xs tw-text-gray-700' }, params.row.customer_code || '—') },
                {
                    title: 'Customer Name', key: 'customer_name_en', minWidth: 180, sortable: 'custom',
                    render: (h, params) => h('span', { class: 'name-text' }, this.isKh
                        ? (params.row.customer_name_kh || params.row.customer_name_en || '—')
                        : (params.row.customer_name_en || params.row.customer_name_kh || '—'))
                },
                { title: 'Contact / Village', key: 'contact_infor', width: 180,
                  render: (h, params) => h('span', { class: 'tw-text-xs tw-text-gray-700' },
                      params.row.contact_infor || '—') },
                { title: 'Cycle Payment', key: 'cycle_payment_date', width: 120, align: 'center', sortable: 'custom',
                  render: (h, params) => h('span', { class: 'tw-text-xs tw-text-gray-600' },
                      params.row.cycle_payment_date ? moment(params.row.cycle_payment_date).format('DD-MMM-YY') : '—') },
                { title: 'Disbursed', key: 'disbursement_date', width: 110, align: 'center', sortable: 'custom',
                  render: (h, params) => h('span', { class: 'tw-text-xs tw-text-gray-600' },
                      params.row.disbursement_date ? moment(params.row.disbursement_date).format('DD-MMM-YY') : '—') },
                { title: 'Maturity', key: 'maturity_date', width: 110, align: 'center', sortable: 'custom',
                  render: (h, params) => h('span', { class: 'tw-text-xs tw-text-gray-600' },
                      params.row.maturity_date ? moment(params.row.maturity_date).format('DD-MMM-YY') : '—') },
                { title: 'Outstanding', key: 'outstanding_amount', width: 150, align: 'right', sortable: 'custom',
                  render: (h, params) => this.moneyCell(params.row.outstanding_amount) },
                { title: 'Pay Off Amt', key: 'pay_off_amount', width: 140, align: 'right', sortable: 'custom',
                  render: (h, params) => this.moneyCell(params.row.pay_off_amount) },
                { title: 'Repayment Amt', key: 'repayment_amount', width: 150, align: 'right', sortable: 'custom',
                  render: (h, params) => this.moneyCell(params.row.repayment_amount) },
                { title: 'Collected', key: 'actual_payment_amount', width: 140, align: 'right', sortable: 'custom',
                  render: (h, params) => this.moneyCell(params.row.actual_payment_amount) },
                { title: 'Paid', key: 'is_paid', width: 90, align: 'center', sortable: 'custom',
                  render: (h, params) => this.paidBadge(params.row) },
                {
                    title: 'CO Name', key: 'co_name_en', minWidth: 160, sortable: 'custom',
                    render: (h, params) => h('span', { class: 'name-text' }, this.isKh
                        ? (params.row.co_name_kh || params.row.co_name_en || '—')
                        : (params.row.co_name_en || params.row.co_name_kh || '—'))
                }
            ]
        }
    },
    methods: {
        ...mapActions('report/resource', ['getEmployee', 'getCurrency']),

        fmt (v) {
            const n = parseFloat(v)
            if (isNaN(n) || n === 0) return '—'
            return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        },
        fmtStrict (v) {
            const n = parseFloat(v)
            if (isNaN(n)) return '0'
            return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        },
        moneyCell (val) {
            const h = this.$createElement
            const n = parseFloat(val)
            if (isNaN(n) || n === 0) return h('span', { class: 'tw-text-gray-300' }, '—')
            return h('span', this.fmt(val))
        },
        paidBadge (row) {
            const h = this.$createElement
            const label = row.is_paid_label || (row.is_paid ? 'Yes' : 'No')
            const cls = row.is_paid
                ? (label === 'Pay Off' ? 'paid-badge paid-badge--payoff' : 'paid-badge paid-badge--yes')
                : 'paid-badge paid-badge--no'
            return h('span', { class: cls }, label)
        },

        summaryMethod ({ columns, data }) {
            const sums = {}
            const pageTotals = {
                outstanding_amount:    0,
                pay_off_amount:        0,
                repayment_amount:      0,
                actual_payment_amount: 0,
                loan_count:            0,
                customer_count:        0
            }
            for (const row of (data || [])) {
                pageTotals.outstanding_amount    += parseFloat(row.outstanding_amount)    || 0
                pageTotals.pay_off_amount        += parseFloat(row.pay_off_amount)        || 0
                pageTotals.repayment_amount      += parseFloat(row.repayment_amount)      || 0
                pageTotals.actual_payment_amount += parseFloat(row.actual_payment_amount) || 0
                pageTotals.loan_count            += parseInt(row.loan_count)              || 0
                pageTotals.customer_count        += parseInt(row.customer_count)          || 0
            }
            columns.forEach((col, idx) => {
                if (idx === 0) { sums[col.key] = { key: col.key, value: 'PAGE TOTAL' }; return }
                let value = ''
                switch (col.key) {
                    case 'loan_count':            value = pageTotals.loan_count.toLocaleString(); break
                    case 'customer_count':        value = pageTotals.customer_count.toLocaleString(); break
                    case 'outstanding_amount':    value = this.fmtStrict(pageTotals.outstanding_amount); break
                    case 'pay_off_amount':        value = this.fmtStrict(pageTotals.pay_off_amount); break
                    case 'repayment_amount':      value = this.fmtStrict(pageTotals.repayment_amount); break
                    case 'actual_payment_amount': value = this.fmtStrict(pageTotals.actual_payment_amount); break
                    default:                      value = ''
                }
                sums[col.key] = { key: col.key, value }
            })
            return sums
        },

        buildParams () {
            const p = {
                branch_id:      this.model.branch_id,
                currency_id:    this.model.currency_id,
                classified_by:  this.model.classified_by,
                paid_filter:    this.model.paid_filter,
                sort:           this.model.sort,
                sort_dir:       this.model.sort_dir,
                page:           this.model.page,
                perPage:        this.perPage,
                excel_template: this.model.excel_template
            }
            if (!this.isBranchMode) p.co_id = this.model.co_id
            if (this.searchQuery && this.searchQuery.trim().length >= 2) {
                p.search = this.searchQuery.trim()
            }
            return p
        },

        fetchData () {
            this.errors = new Errors()
            this.loading = true
            this.summaryLoading = true

            this.$store
                .dispatch('report/creditOperation/dailyCollectionScheduleV3', this.buildParams())
                .then(response => {
                    const data    = response.data || []
                    const meta    = response.meta || response
                    const summary = response.summary || null
                    const totals  = response.totals  || null
                    const mode    = response.mode    || this.model.classified_by

                    this.rows = data
                    this.mode = mode
                    this.meta = {
                        current_page: meta.current_page || 1,
                        last_page:    meta.last_page    || 1,
                        total:        meta.total ?? 0
                    }

                    if (summary) {
                        this.summary = {
                            all:       Object.assign({ count: 0, total: '0' }, summary.all       || {}),
                            collected: Object.assign({ count: 0, total: '0' }, summary.collected || {}),
                            pending:   Object.assign({ count: 0, total: '0' }, summary.pending   || {}),
                            customers: Object.assign({ count: 0 },             summary.customers || {})
                        }
                    }
                    if (totals) {
                        this.totals = Object.assign({}, this.totals, totals)
                    }
                })
                .catch(error => {
                    this.notice({ type: 'error', text: (error && error.message) || 'Failed to load report' })
                    this.errors = new Errors((error && error.errors) || {})
                    this.rows = []
                })
                .finally(() => {
                    this.loading = false
                    this.summaryLoading = false
                })
        },

        onSearch () {
            this.model.page = 1
            this.filtersOpen = false
            this.fetchData()
        },

        onClearSearch () {
            const hadSearch = !!(this.searchQuery && this.searchQuery.trim())
            this.searchQuery = ''
            if (hadSearch && this.meta.total !== null) {
                this.model.page = 1
                this.fetchData()
            }
        },

        onReset () {
            this.searchQuery = ''
            this.model.co_id = []
            this.model.classified_by = 'co'
            this.model.paid_filter = 'all'
            this.model.sort = 'customer_code'
            this.model.sort_dir = 'asc'
            this.model.page = 1
            this.rows = []
            this.meta = { current_page: 1, last_page: 1, total: null }
        },

        onSortChange ({ key, order }) {
            if (order === 'normal') {
                this.model.sort = this.isBranchMode ? 'branch_code' : 'customer_code'
                this.model.sort_dir = 'asc'
            } else {
                this.model.sort = key
                this.model.sort_dir = order === 'asc' ? 'asc' : 'desc'
            }
            this.model.page = 1
            this.fetchData()
        },

        onPageChange (page) {
            this.model.page = page
            this.fetchData()
        },
        onPerPageChange (v) {
            this.perPage = v
            this.model.page = 1
            this.fetchData()
        },

        exportExcel () {
            this.errors = new Errors()
            this.exporting = true

            this.$store
                .dispatch('report/creditOperation/dailyCollectionScheduleV3ExportExcel', this.buildParams())
                .then(response => {
                    const filename = trim(
                        response.headers['content-disposition']
                            .substring(response.headers['content-disposition'].indexOf('filename'))
                            .replace('filename=', ''),
                        '"'
                    )
                    FileDownload(response.data, filename)
                })
                .catch(err => {
                    let msg = 'Export failed'
                    try {
                        const parsed = JSON.parse(String.fromCharCode.apply(null, new Uint8Array(err)))
                        msg = parsed.message || msg
                        this.errors = new Errors(parsed.errors || {})
                    } catch (e) { /* not JSON */ }
                    this.notice({ type: 'error', text: msg })
                })
                .finally(() => { this.exporting = false })
        },

        notice (not) {
            this.$Notice[not.type]({
                title: 'DAILY COLLECTION SCHEDULE',
                desc: not.text
            })
        }
    },
    watch: {
        'model.branch_id': debounce(function (value) {
            this.model.co_id = []
            this.getEmployee({ branch_id: value })
        }, 500),
        'model.classified_by' () {
            this.model.sort = this.isBranchMode ? 'branch_code' : 'customer_code'
            this.model.sort_dir = 'asc'
            this.model.co_id = []
            this.rows = []
            this.meta = { current_page: 1, last_page: 1, total: null }
            this.model.page = 1
        }
    },
    mounted () {
        this._onResize = debounce(() => { this.viewportHeight = window.innerHeight }, 100)
        window.addEventListener('resize', this._onResize)
    },
    beforeDestroy () {
        if (this._onResize) window.removeEventListener('resize', this._onResize)
    },
    beforeRouteEnter (to, from, next) {
        next(vm => { vm.getCurrency() })
    }
}
</script>

<style scoped>
.filter-grid {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 12px;
    align-items: end;
}
.filter-cell { min-width: 0; }
.filter-lbl {
    display: block; font-size: 11px; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.03em;
    color: #6b7280; margin-bottom: 4px; height: 15px;
}
::v-deep .ivu-select-selection { min-height: 34px; }
::v-deep .ivu-input { height: 34px; }

.pill-group {
    display: inline-flex; align-items: center; flex-wrap: nowrap; gap: 3px;
    padding: 3px; background: #f3f4f6; border-radius: 6px;
    height: 34px; box-sizing: border-box; overflow-x: auto; max-width: 100%;
}
.pill {
    background: transparent; border: none; padding: 4px 10px;
    font-size: 12px; color: #4b5563; border-radius: 4px;
    cursor: pointer; transition: all 0.15s; line-height: 1.2;
    white-space: nowrap; display: inline-flex; align-items: center;
}
.pill:hover { background: #e5e7eb; }
.pill-active {
    background: #ffffff; color: #111827; font-weight: 600;
    box-shadow: 0 1px 2px rgba(0,0,0,0.08);
}

.chip-pill {
    display: inline-flex; align-items: center; padding: 2px 8px;
    font-size: 11px; border-radius: 10px;
    background: #f3f4f6; color: #374151; line-height: 1.5; white-space: nowrap;
}
.chip-default  { background: #f3f4f6; color: #374151; }
.chip-branch   { background: #eef2ff; color: #4338ca; }
.chip-currency { background: #ecfdf5; color: #047857; }
.chip-type     { background: #dbeafe; color: #1e40af; }
.chip-co       { background: #f3e8ff; color: #6b21a8; }
.chip-search   { background: #fce7f3; color: #9d174d; }

@media (max-width: 991px) {
    .filter-grid > .filter-cell { grid-column: span 12 !important; }
}

.search-group {
    display: flex; align-items: stretch; height: 34px;
    border: 1px solid #d1d5db; border-radius: 6px;
    overflow: hidden; background: #ffffff;
}
.search-input-wrap { position: relative; flex: 1; display: flex; align-items: stretch; min-width: 0; }
.search-input {
    flex: 1; border: none !important; height: 100% !important;
    padding: 0 32px 0 12px; outline: none; background: transparent;
    font-size: 13px; color: #111827; min-width: 0;
}
.search-input:focus { box-shadow: none !important; }
.search-clear {
    position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
    background: transparent; border: none; padding: 4px;
    font-size: 14px; color: #9ca3af; cursor: pointer;
}
.search-btn {
    background: #3b82f6; color: white; border: none;
    padding: 0 16px; font-size: 13px; font-weight: 600;
    cursor: pointer; display: inline-flex; align-items: center;
}
.search-btn:hover:not(:disabled) { background: #2563eb; }
.search-btn:disabled { background: #93c5fd; cursor: wait; }
.reset-btn {
    background: #f9fafb; color: #6b7280; border: none;
    border-left: 1px solid #e5e7eb; padding: 0 12px; cursor: pointer;
}
.reset-btn:hover { background: #f3f4f6; color: #374151; }

.results-card {
    background: #ffffff; border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); overflow: hidden;
}
.results-toolbar {
    display: flex; justify-content: space-between; align-items: center;
    padding: 12px 16px; border-bottom: 1px solid #e5e7eb;
    background: #ffffff; gap: 8px; flex-wrap: wrap;
}
.results-body { min-height: 120px; }
.results-pagination {
    display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center;
    padding: 10px 16px; border-top: 1px solid #e5e7eb;
    background: #f9fafb; gap: 8px; min-height: 52px;
}
.pagination-left {
    display: flex; align-items: center; gap: 10px;
    height: 28px; line-height: 28px; font-size: 13px; color: #6b7280;
}
.pagination-lbl { display: inline-flex; align-items: center; height: 28px; line-height: 28px; white-space: nowrap; }
.pagination-lbl b { color: #111827; font-weight: 600; margin: 0 2px; }
.pagination-divider { color: #d1d5db; line-height: 28px; }

.summary-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
}
@media (max-width: 991px) { .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 575px) { .summary-grid { grid-template-columns: 1fr; } }
.summary-card {
    display: flex; flex-direction: column; justify-content: space-between; gap: 8px;
    padding: 12px 14px; background: #ffffff;
    border: 1px solid #e5e7eb; border-top: 3px solid;
    border-radius: 6px; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    min-height: 110px;
}
.summary-card__header { display: flex; align-items: center; gap: 8px; }
.summary-card__icon {
    width: 22px; height: 22px; display: flex; align-items: center; justify-content: center;
    border-radius: 4px; font-size: 11px; flex-shrink: 0;
}
.summary-card__label {
    flex: 1; font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.03em; color: #6b7280; line-height: 1.2;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.summary-card__count {
    font-size: 11px; font-weight: 600; color: #9ca3af;
    padding: 1px 6px; background: #f3f4f6; border-radius: 10px; line-height: 1.4;
}
.summary-card__value {
    display: flex; align-items: baseline; gap: 6px;
    line-height: 1; white-space: nowrap; overflow: hidden;
}
.summary-card__amount {
    font-size: 20px; font-weight: 700; color: #111827;
    overflow: hidden; text-overflow: ellipsis; min-width: 0;
}
.summary-card__currency {
    font-size: 11px; font-weight: 600; color: #9ca3af;
    letter-spacing: 0.03em; flex-shrink: 0;
}
.summary-card--cur_due  { border-top-color: #6366f1; }
.summary-card--cur_due  .summary-card__icon { background: #eef2ff; color: #4338ca; }
.summary-card--cur_paid { border-top-color: #10b981; }
.summary-card--cur_paid .summary-card__icon { background: #ecfdf5; color: #047857; }
.summary-card--early    { border-top-color: #3b82f6; }
.summary-card--early    .summary-card__icon { background: #eff6ff; color: #1e40af; }
.summary-card--past_due { border-top-color: #ef4444; }
.summary-card--past_due .summary-card__icon { background: #fee2e2; color: #991b1b; }

::v-deep .ivu-table th {
    background: #f9fafb !important;
    height: 40px; color: #374151; font-weight: 600;
    font-size: 12px; text-transform: uppercase; letter-spacing: 0.02em;
}
::v-deep .ivu-table th .ivu-table-cell {
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
::v-deep .ivu-table-summary td {
    background: #f9fafb !important; font-weight: 700 !important;
    color: #111827 !important; border-top: 2px solid #d1d5db !important;
}
::v-deep .ivu-table td .ivu-table-cell {
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; padding: 4px 12px;
}
::v-deep .ivu-table td { height: 34px; }
::v-deep .ivu-table-small td { height: 32px; }

.export-group {
    display: inline-flex; align-items: stretch; height: 32px;
    border-radius: 5px; overflow: hidden;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    border: 1px solid #d1d5db; background: #ffffff;
}
.export-select {
    border: 0; border-right: 1px solid #d1d5db;
    background: #ffffff; padding: 0 28px 0 12px;
    font-size: 12px; color: #374151; font-weight: 500;
    cursor: pointer; outline: none; height: 100%;
    appearance: none; -webkit-appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3e%3cpath fill='%236b7280' d='M5 6L0 0h10z'/%3e%3c/svg%3e");
    background-repeat: no-repeat; background-position: right 10px center; background-size: 10px 6px;
}
.export-btn {
    background: #10b981; color: white; border: none;
    padding: 0 16px; font-size: 13px; font-weight: 600;
    cursor: pointer; display: inline-flex; align-items: center;
}
.export-btn:hover:not(:disabled) { background: #059669; }
.export-btn:disabled { background: #e5e7eb; color: #9ca3af; cursor: not-allowed; }

.empty-state {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 40px 20px; text-align: center; height: 100%; position: absolute; inset: 0;
}
</style>

<!-- Unscoped: render-function elements don't receive data-v-xxx. -->
<style>
.name-text {
    color: #111827; font-size: 12px; font-weight: 500;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.branch-code {
    display: inline-block; padding: 1px 6px;
    font-size: 11px; font-weight: 600;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    color: #4338ca; background: #eef2ff;
    border: 1px solid #e0e7ff; border-radius: 3px;
    letter-spacing: 0.02em;
}
.paid-badge {
    display: inline-block; padding: 1px 8px;
    font-size: 11px; font-weight: 600; border-radius: 10px;
    letter-spacing: 0.02em;
}
.paid-badge--yes    { color: #047857; background: #ecfdf5; border: 1px solid #a7f3d0; }
.paid-badge--no     { color: #991b1b; background: #fef2f2; border: 1px solid #fecaca; }
.paid-badge--payoff { color: #1e40af; background: #eff6ff; border: 1px solid #bfdbfe; }
</style>
